// The few product events PostHog gets (only with consent, see consent.ts). They come from one place: the API client's
// fetch, so a screen never has to remember to report. Only the moments that tell whether a production gets made are
// kept (free-tier sized): a production, its episodes, elements and shots, paid work started, a take picked, a request
// for sign-off and the sign-off, review notes, the cut exported, crew invited, a checkout opened and Claude connected.
// Properties are a fixed vocabulary (a department, where work started): never an id, a name or anything typed.

export interface TrackedEvent {
  event: string;
  props?: Record<string, string>;
}

const ID = "[^/]+";
const RULES: [RegExp, (m: RegExpMatchArray) => TrackedEvent][] = [
  [/^\/projects$/, () => ({ event: "production_created" })],
  [/^\/episodes$/, () => ({ event: "episode_created" })],
  [/^\/elements$/, () => ({ event: "element_created" })],
  [/^\/shots$/, () => ({ event: "shot_created" })],
  [
    new RegExp(`^/(shots|scenes)/${ID}/generations$`),
    (m) => ({ event: "generation_started", props: { from: m[1] === "shots" ? "shot" : "scene" } }),
  ],
  [
    new RegExp(`^/canvases/${ID}/nodes/${ID}/run$`),
    () => ({ event: "generation_started", props: { from: "canvas" } }),
  ],
  [
    new RegExp(`^/takes/${ID}/(reroll|revise)$`),
    (m) => ({ event: "generation_started", props: { from: m[1]! } }),
  ],
  [new RegExp(`^/(shots|scenes)/${ID}/selections$`), () => ({ event: "take_picked" })],
  [new RegExp(`^/takes/${ID}/accept$`), () => ({ event: "take_picked" })],
  [
    new RegExp(`^/episodes/${ID}/steps/(${ID})/requests$`),
    (m) => ({ event: "signoff_requested", props: { department: m[1]! } }),
  ],
  [
    new RegExp(`^/episodes/${ID}/steps/(${ID})/signoffs$`),
    (m) => ({ event: "signed_off", props: { department: m[1]! } }),
  ],
  [/^\/threads$/, () => ({ event: "note_posted" })],
  [new RegExp(`^/threads/${ID}/messages$`), () => ({ event: "note_posted" })],
  [new RegExp(`^/episodes/${ID}/cut/exports$`), () => ({ event: "cut_exported" })],
  [/^\/invitations$/, () => ({ event: "crew_invited" })],
  [
    /^\/billing\/(checkout|packs)$/,
    (m) => ({ event: "checkout_started", props: { kind: m[1] === "packs" ? "pack" : "plan" } }),
  ],
  [/^\/(connections|oauth\/approve)$/, () => ({ event: "claude_connected" })],
];

/** The API path after `/v1` and after `/workspaces/<id>`, without the query. */
function relative(url: string): string {
  let path = url.split("?")[0] ?? "";
  try {
    path = new URL(path, "http://x").pathname;
  } catch {
    // keep it as it is
  }
  return path.replace(/^.*?\/v1(?=\/)/, "").replace(/^\/workspaces\/[^/]+(?=\/)/, "");
}

/** The event a successful request stands for, or null (most requests are not worth one). */
export function eventFor(method: string, url: string): TrackedEvent | null {
  if (method.toUpperCase() !== "POST") return null;
  const path = relative(url);
  for (const [re, make] of RULES) {
    const m = path.match(re);
    if (m) return make(m);
  }
  return null;
}

/** A route with its ids taken out, so server errors group per endpoint: `/shots/:id/generations`. */
export function routeOf(url: string): string {
  const parts = relative(url).split("/");
  return parts
    .map((s, i) =>
      // a review link's token is a secret, never an id to group by
      parts[i - 1] === "share" && i === 2
        ? ":token"
        : /^[0-9a-f-]{16,}$/i.test(s) || /^\d+$/.test(s)
          ? ":id"
          : s,
    )
    .join("/");
}

/** What a finished request reports: its event when it worked, `api_error` when the server failed (5xx). */
export function eventsForResponse(method: string, url: string, status: number): TrackedEvent | null {
  if (status >= 500)
    return {
      event: "api_error",
      props: { status: String(status), method: method.toUpperCase(), route: routeOf(url) },
    };
  if (status >= 200 && status < 300) return eventFor(method, url);
  return null;
}
